import { Box, Grid } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useDraggable } from '@dnd-kit/core';
import { VisibilityOff } from '@mui/icons-material';
import { useAuth } from '../../session/InternalAuthProvider';
import { getOrderPage, getOrderPages } from '../../common/AccountUtils';
import LinearProgressWithLabel from '../../common/ui/LinearProgressWithLabel';
import { useCalendar } from '../CalendarContext/CalendarContext';
import { useLanguage } from '../../common/GeneralUtils';
import { AccountData, EnrichedEventData } from '../../api';
import { getEmployeeNames } from '../CalendarUtils';
import { getTimeFormat } from '../../common/getDateAndTimeFormat';
import { CalendarBoxProvider, CalendarBoxTypography } from './CalendarBoxContext';

type EventBoxProps = {
  event: EnrichedEventData;
  css?: any;
  viewType: 'month' | 'day' | 'condensed';
  openEvent: (event: EnrichedEventData, e: any) => void;
  shouldNotDragNDrop?: boolean;
};

const EventBox = ({ event, css, viewType, openEvent, shouldNotDragNDrop }: EventBoxProps) => {
  // @ts-ignore
  const { authState } = useAuth();
  const [highlight, setHighlight] = useState(false);
  const timeFormat = getTimeFormat(authState?.account || undefined);

  const account = authState.account as unknown as AccountData;
  const accountLocations = account.locations;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: event.id,
  });
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const queryOrderId = urlParams.get('entityid');
    if (queryOrderId && queryOrderId === event.id) {
      setHighlight(true);
      setTimeout(() => {
        setHighlight(false);
        urlParams.delete('entityid');
        window.history.replaceState(null, '', `${window.location.pathname}?${urlParams.toString()}`);
      }, 3000); // Duration of the gradient animation
    }
  }, [event.id]);
  const language = useLanguage();

  const orderPage = getOrderPage(authState.account, event.order_page_id);
  const shouldShowRedBorder = event.employee_ids.length === 0;
  const shouldHideFromCustomers = event.hidden_from_customers;
  // @ts-ignore
  const { users, setShouldBlockSwiping } = useCalendar();
  useEffect(() => {
    setShouldBlockSwiping(isDragging);
  }, [isDragging, setShouldBlockSwiping]);
  const hasOnlyOneOrderPage = getOrderPages(account).length === 1;
  const customUsedTickets = event.custom_ticket_usages?.reduce((total, usage) => total + usage.used_tickets, 0) || 0;
  const usedTickets = event.used_tickets + customUsedTickets;
  const isCompact = event.duration <= 3600; // 1 hour or less

  const getTimeText = () => {
    if (!event.time) {
      return '';
    }
    return `${moment.unix(event.time).tz('utc').format(timeFormat)} - ${moment
      .unix(event.time + event.duration)
      .tz('utc')
      .format(timeFormat)}`;
  };
  const formatEmployees = () => {
    if (event.employee_ids.length) {
      return getEmployeeNames(event.employee_ids, users).map((x) => (
        <Grid key={`employee-${x}`}>
          <CalendarBoxTypography>{x}</CalendarBoxTypography>
        </Grid>
      ));
    }
    return '';
  };

  // Create complete style objects for each view type
  const getBorder = () =>
    shouldHideFromCustomers
      ? '2px solid orange'
      : shouldShowRedBorder
      ? '2px solid red'
      : '1px solid rgb(230, 230, 230)';

  const getCompleteStyles = () => {
    const baseStyles = {
      backgroundColor: orderPage.color || '#6577b3',
      color: 'white',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '12px',
      border: getBorder(),
      opacity: usedTickets === 0 ? '0.6' : '1',
      ...(highlight && {
        animation: 'gradient-animation 3s ease-in-out',
        '@keyframes gradient-animation': {
          '0%': {
            background: 'linear-gradient(135deg, #2b324c, #67ad5b)',
          },
        },
      }),
    };

    if (viewType === 'month') {
      return {
        ...baseStyles,
        position: 'static !important',
        marginLeft: '8px',
        marginRight: '8px',
        '@media (max-width: 600px)': {
          marginLeft: 0,
          marginRight: 0,
        },
      };
    }

    if (viewType === 'condensed') {
      return {
        ...baseStyles,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
      };
    }

    // Day view
    const dayBaseStyles = {
      ...baseStyles,
      position: 'absolute' as const,
      width: `calc(${css.width}% - 2px)`,
      left: `${css.left}%`,
      top: `${css.top}px`,
      height: `${css.height}px`,
      marginRight: '1px',
      marginLeft: '1px',
      transition: isDragging ? 'none' : 'transform 200ms ease',
      zIndex: isDragging ? 1000 : 'auto',
      ...(transform && { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }),
    };

    if (isCompact) {
      return {
        ...dayBaseStyles,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
      };
    }

    return dayBaseStyles;
  };

  // Render visibility icon for hidden events
  const renderVisibilityIcon = () =>
    shouldHideFromCustomers && (
      <VisibilityOff
        sx={{
          width: '20px',
          position: 'absolute',
          left: '4px',
          top: '4px',
          zIndex: 2,
        }}
      />
    );

  // Render event custom details (equivalent to CustomDetailRenderer for orders)
  const renderEventCustomDetails = () => {
    if (isEmpty(event.custom_details)) return null;

    return (
      <Grid item sx={{ position: 'absolute', top: 0, left: 0, zIndex: 1, fontSize: '10px' }}>
        {event.custom_details[0].label[language]}
      </Grid>
    );
  };

  // Content based on view type and size
  const renderContent = () => {
    const showTime = viewType === 'month' || viewType === 'condensed';
    const showDate = viewType === 'condensed';
    const showLocation = accountLocations.length > 1;
    const showProgressBar = true;
    const showEventLabel = !hasOnlyOneOrderPage || viewType !== 'condensed';

    if (isCompact && viewType === 'day') {
      // Compact day view - minimal content
      return (
        <Grid container alignItems="center" justifyContent="center" gap={0.5} p={0.5} sx={{ position: 'relative' }}>
          {renderVisibilityIcon()}
          {renderEventCustomDetails()}
          <Grid container item justifyContent="center" flexWrap="nowrap" alignItems="center" gap={0.5}>
            {formatEmployees()}
          </Grid>
          <Grid item sx={{ width: '100%', textAlign: 'center' }}>
            <CalendarBoxTypography>{orderPage.label[language].slice(0, 30)}</CalendarBoxTypography>
          </Grid>
        </Grid>
      );
    }

    if (viewType === 'condensed') {
      // Condensed view - special layout with progress bar at bottom
      return (
        <Grid item container flexDirection="column">
          <Grid container p={1} alignItems="center" flexDirection="column" sx={{ position: 'relative' }}>
            {renderVisibilityIcon()}
            {renderEventCustomDetails()}
            {showDate && (
              <Grid dir="ltr" sx={{ width: '100%', textAlign: 'center' }}>
                <CalendarBoxTypography variant="subtitle2">
                  {moment(event.date).format('ddd, MM.DD')}
                </CalendarBoxTypography>
              </Grid>
            )}
            {showTime && (
              <Grid dir="ltr" sx={{ width: '100%', textAlign: 'center' }}>
                <CalendarBoxTypography variant="subtitle2">{getTimeText()}</CalendarBoxTypography>
              </Grid>
            )}
            <Grid container item justifyContent="center" flexWrap="nowrap" alignItems="center" gap={0.5}>
              {formatEmployees()}
            </Grid>
            {showEventLabel && (
              <Grid item sx={{ width: '100%', textAlign: 'center' }}>
                <CalendarBoxTypography sx={{ lineHeight: 1.2 }}>
                  {orderPage.label[language].slice(0, 50)}
                </CalendarBoxTypography>
              </Grid>
            )}
            {showLocation && (
              <CalendarBoxTypography>
                {accountLocations.find((x) => x.id === event.location_id)?.name?.[language]}
              </CalendarBoxTypography>
            )}
          </Grid>
          <Grid
            item
            pt={1}
            pb={0.5}
            alignItems="center"
            container
            sx={{
              background: 'rgba(255, 255, 255, 0.2)',
              width: '100%',
              boxShadow: '0px -1px 8px rgb(30 30 30 / 15%)',
            }}>
            <LinearProgressWithLabel value={usedTickets} total={event.tickets} />
          </Grid>
        </Grid>
      );
    }

    // Month and full day view - standard layout
    const content = (
      <Grid
        container
        flexDirection="column"
        alignItems={viewType === 'month' ? 'center' : 'flex-start'}
        justifyContent={viewType === 'month' ? 'center' : 'flex-start'}
        gap={0.5}
        p={viewType === 'month' ? undefined : 0.5}
        sx={{
          ...(viewType === 'month' && {
            paddingTop: '4px',
            paddingBottom: '4px',
            textAlign: 'center',
          }),
          ...(viewType === 'day' && { height: '100%' }),
          position: 'relative',
        }}>
        {renderVisibilityIcon()}
        {renderEventCustomDetails()}
        {showTime && (
          <Grid dir="ltr" sx={{ width: '100%', textAlign: 'center' }}>
            <CalendarBoxTypography>{getTimeText()}</CalendarBoxTypography>
          </Grid>
        )}
        <Grid container item justifyContent="center" flexWrap="nowrap" alignItems="center" gap={0.5}>
          {formatEmployees()}
        </Grid>
        <Grid item sx={{ width: '100%', textAlign: 'center' }}>
          <CalendarBoxTypography sx={{ lineHeight: 1.2 }}>
            {orderPage.label[language].slice(0, 50)}
          </CalendarBoxTypography>
        </Grid>
        {showLocation && (
          <Grid item sx={{ width: '100%', textAlign: 'center' }}>
            <CalendarBoxTypography>
              {accountLocations.find((x) => x.id === event.location_id)?.name?.[language]}
            </CalendarBoxTypography>
          </Grid>
        )}
        {viewType === 'month' && showProgressBar && (
          <Grid container>
            <LinearProgressWithLabel value={usedTickets} total={event.tickets} />
          </Grid>
        )}
      </Grid>
    );

    // Full day view with bottom progress bar
    if (viewType === 'day' && !isCompact) {
      return (
        <>
          {content}
          <Grid
            container
            sx={{
              position: 'absolute',
              height: '28px',
              bottom: 0,
              background: 'rgba(255, 255, 255, 0.2)',
              width: '100%',
              boxShadow: '0px -1px 8px rgb(30 30 30 / 15%)',
            }}>
            <LinearProgressWithLabel value={usedTickets} total={event.tickets} />
          </Grid>
        </>
      );
    }

    return content;
  };

  // Shared container with drag & drop and click handling
  const containerProps =
    viewType === 'month' || viewType === 'condensed'
      ? {}
      : {
          ref: shouldNotDragNDrop ? undefined : setNodeRef,
          ...(shouldNotDragNDrop ? {} : attributes),
          ...(shouldNotDragNDrop ? {} : listeners),
        };

  const handleClick = (e: any) => {
    if (viewType === 'month' || viewType === 'condensed' || !isDragging) {
      openEvent(event, e);
    }
  };

  const finalStyles = getCompleteStyles();

  return (
    <CalendarBoxProvider
      cssCalculatedWidth={css?.width}
      backgroundColor={orderPage?.color || '#6577b3'}
      viewType={viewType}>
      <Box {...containerProps} onClick={handleClick} sx={finalStyles}>
        {renderContent()}
      </Box>
    </CalendarBoxProvider>
  );
};

export default EventBox;
